import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useBeforeUnload, useBlocker } from "react-router-dom";

const DraftContext = createContext<((id: string, dirty: boolean) => void) | null>(null);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [dirtyForms, setDirtyForms] = useState<Set<string>>(() => new Set());
  const register = useCallback((id: string, dirty: boolean) => {
    setDirtyForms((current) => {
      if (current.has(id) === dirty) return current;
      const next = new Set(current);
      if (dirty) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const dirty = dirtyForms.size > 0;
  const blocker = useBlocker(dirty);
  const prompted = useRef(false);
  useBeforeUnload(
    useCallback(
      (event) => {
        if (!dirty) return;
        event.preventDefault();
        event.returnValue = "";
      },
      [dirty],
    ),
  );
  useEffect(() => {
    if (blocker.state !== "blocked") {
      prompted.current = false;
      return;
    }
    if (prompted.current) return;
    prompted.current = true;
    if (window.confirm("Leave without saving your changes?")) blocker.proceed();
    else blocker.reset();
  }, [blocker]);
  return <DraftContext.Provider value={register}>{children}</DraftContext.Provider>;
}

export function useUnsavedChanges(dirty: boolean) {
  const register = useContext(DraftContext);
  const id = useId();
  useEffect(() => {
    register?.(id, dirty);
    return () => register?.(id, false);
  }, [register, id, dirty]);
}
